// ─── Invoice diff helpers ──────────────────────────────────────────────────
//
// Pure helpers that classify each line on a freshly-parsed invoice against
// the operator's purchase history, producing a `parse_diff` JSON blob per
// line and a roll-up `parse_summary` per invoice. These two blobs are what
// the live toast + Invoice Review modal render — no recomputation at view
// time, ever.
//
// Architectural parallel: this file is to invoice-parse-notification what
// `src/lib/recipeHealth.js` is to Recipe Health. Pure functions, no I/O,
// no React. Persistence is the caller's responsibility (FoodPriceTracker
// writes the rows; App.js reads them back via realtime push).
//
// ── Diff classes (5 total) ────────────────────────────────────────────────
//   flat          — |delta_pct| < FLAT_THRESHOLD_PCT against the baseline.
//                   Most lines on most invoices. Collapsed to a tail-row in
//                   the modal so the operator's eye lands on what moved.
//   up            — price went up. Eyebrow turns amber. If the same
//                   food_item has gone up on 3+ consecutive invoices
//                   (consecutive_increases ≥ 3) the eyebrow escalates to
//                   red — that's the kill-step that catches a vendor
//                   slowly raising your beef chuck a buck a week.
//   down          — price went down. Renders neutral; we don't crow about
//                   it, but the operator should know.
//   pack_changed  — quantity-per-pack OR piece_weight differs from the
//                   mode of the last 3 purchases of this food_item.
//                   "50# → 45#" pill. This is the killer-feature visual
//                   for the landing page — vendors silently shrink the
//                   pack and the unit price looks unchanged.
//   new_item      — no prior purchase of this food_item. Renders as a
//                   neutral pill; nothing to compare against.
//
// ── Baseline selection ────────────────────────────────────────────────────
//   1. last_buy_same_vendor — most recent line for this food_item from
//      the same vendor within VENDOR_BASELINE_DAYS (90d). Preferred
//      because vendor-to-vendor price drift is noise the operator
//      already accepted when they switched suppliers.
//   2. rolling_90d_avg — fallback if no same-vendor history. Average
//      price_per_lb across all vendors over the last 90 days.
//   3. null → classified as new_item.
//
// ── consecutive_increases ─────────────────────────────────────────────────
// For each food_item we walk back through the operator's last N=10
// invoices for that item (across vendors, since the operator wants to
// know "is my chuck creeping up regardless of who I buy from"). We count
// the longest consecutive-up streak ending at the current invoice. That
// number is stamped onto parse_diff.consecutive_increases and the toast/
// modal use it to pick eyebrow tone.
//
// Cap at N=10 to avoid quadratic work on operators with rich history.
// The escalation threshold is fixed at 3 — past that we don't need
// finer granularity, "trend confirmed" is enough.
//
// ── Why price_per_lb over unit_price ──────────────────────────────────────
// price_per_lb normalizes pack/unit changes — a 50# bag at $1/lb costing
// $50 vs a 45# bag at $1/lb costing $45 has unchanged price_per_lb but
// the unit_price dropped 10%. We compare on price_per_lb; the
// pack_changed flag captures the volume shift separately so the operator
// doesn't see "price down 10%!" when the vendor just shrunk the bag.
// If price_per_lb is null on either side we fall back to unit_price.
//
// All amounts in this file are USD; all dates are ISO yyyy-mm-dd. Pure
// JS, no external deps.

// Treat anything within ±2% as flat. Restaurant invoices commonly bounce
// 0.5–1.5% on rounding, fuel surcharges, and tier-pricing thresholds.
// 2% is the band most operators describe as "noise, don't bother me."
export const FLAT_THRESHOLD_PCT = 0.02;

// Vendor baseline lookback. Beyond 90 days the price signal is too stale
// to call this a real "change" — we want recency.
export const VENDOR_BASELINE_DAYS = 90;

// Cap the consecutive-up walk at the last 10 invoices for this item.
// Beyond that we don't need finer granularity for the eyebrow tone.
export const CONSECUTIVE_INCREASES_CAP = 10;

// Threshold that escalates an `up` line from amber to red.
export const CONSECUTIVE_RED_THRESHOLD = 3;

// Pack-change detection looks at the mode of the last 3 purchases.
// Use 3 to ride out a one-off vendor substitution; the 4th purchase
// in a new pack confirms it's the new normal.
export const PACK_HISTORY_WINDOW = 3;

// ───────────────────────────────────────────────────────────────────────────
// Internal utilities

function pctDelta(current, baseline) {
  if (baseline == null || baseline === 0 || current == null) return null;
  return (Number(current) - Number(baseline)) / Number(baseline);
}

function daysBetween(dateA, dateB) {
  const a = new Date(dateA);
  const b = new Date(dateB);
  return Math.abs((a - b) / (1000 * 60 * 60 * 24));
}

function modeOf(values) {
  // Returns the most common value, or the most-recent on tie. `values`
  // should be passed in newest-first; that ordering breaks ties toward
  // the operator's most recent reality.
  if (!values || values.length === 0) return null;
  const counts = new Map();
  for (const v of values) {
    if (v == null) continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  if (counts.size === 0) return null;
  let best = null;
  let bestCount = -1;
  // Iterate in insertion order (newest-first); first to reach max wins ties.
  for (const [v, c] of counts) {
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  }
  return best;
}

function priceForCompare(line) {
  // Prefer price_per_lb (normalized across pack changes); fall back to
  // unit_price if the parser couldn't normalize.
  if (line == null) return null;
  if (line.price_per_lb != null && Number(line.price_per_lb) > 0) {
    return Number(line.price_per_lb);
  }
  if (line.unit_price != null) return Number(line.unit_price);
  return null;
}

// ───────────────────────────────────────────────────────────────────────────
// computeLineDiff
//
// Classify a single freshly-parsed invoice line against history.
//
// @param line              the new invoice_line_items row (must include
//                          food_item_id, vendor_name, invoice_date,
//                          price_per_lb, unit_price, pack_count, piece_weight)
// @param history           array of prior invoice_line_items rows for the
//                          same food_item_id, ANY vendor, ordered newest-
//                          first. Pre-filtered to lines with the same
//                          food_item_id.
// @param vendorHistory     array of prior invoice_line_items rows for the
//                          same food_item_id AND same vendor_name within
//                          the last VENDOR_BASELINE_DAYS. Newest-first.
// @param currentInvoice    the parent invoice row (for invoice_date).
//
// @returns parse_diff JSON blob, ready to write to invoice_line_items.parse_diff:
//   {
//     class: 'flat'|'up'|'down'|'pack_changed'|'new_item',
//     baseline_price: number|null,
//     baseline_source: 'vendor'|'rolling_90d'|null,
//     current_price: number|null,
//     delta_abs: number|null,
//     delta_pct: number|null,           // -0.082 for "8.2% drop"
//     pack_changed: { from: '50#', to: '45#' } | null,
//     consecutive_increases: number,    // 0 if not on an up streak
//     baseline_invoice_id: number|null, // for traceability
//     baseline_invoice_date: string|null,
//   }
export function computeLineDiff(line, history, vendorHistory, currentInvoice) {
  const currentPrice = priceForCompare(line);
  const currentInvoiceDate = currentInvoice?.invoice_date || line.invoice_date;

  // ── Pack change detection ──────────────────────────────────────────────
  // Mode of pack_count + piece_weight from the last PACK_HISTORY_WINDOW
  // purchases of this food_item (any vendor — operators care about the
  // physical pack regardless of supplier).
  const recent = (history || []).slice(0, PACK_HISTORY_WINDOW);
  const modePack = modeOf(recent.map(r => r.pack_count));
  const modePiece = modeOf(recent.map(r => r.piece_weight));
  const packChanged =
    recent.length > 0 &&
    ((modePack != null && line.pack_count != null && Number(line.pack_count) !== Number(modePack)) ||
     (modePiece != null && line.piece_weight != null && String(line.piece_weight) !== String(modePiece)));

  let packChangedBlock = null;
  if (packChanged) {
    const fromLabel = formatPackLabel(modePack, modePiece);
    const toLabel = formatPackLabel(line.pack_count, line.piece_weight);
    packChangedBlock = { from: fromLabel, to: toLabel };
  }

  // ── New item check ─────────────────────────────────────────────────────
  if (!history || history.length === 0) {
    return {
      class: 'new_item',
      baseline_price: null,
      baseline_source: null,
      current_price: currentPrice,
      delta_abs: null,
      delta_pct: null,
      pack_changed: packChangedBlock, // virtually never set on new_item but harmless
      consecutive_increases: 0,
      baseline_invoice_id: null,
      baseline_invoice_date: null,
    };
  }

  // ── Baseline selection ─────────────────────────────────────────────────
  // Prefer the most recent same-vendor line within VENDOR_BASELINE_DAYS.
  let baselineLine = null;
  let baselineSource = null;
  if (vendorHistory && vendorHistory.length > 0) {
    const recentVendor = vendorHistory.find(r => {
      if (!r.invoice_date || !currentInvoiceDate) return false;
      return daysBetween(r.invoice_date, currentInvoiceDate) <= VENDOR_BASELINE_DAYS;
    });
    if (recentVendor) {
      baselineLine = recentVendor;
      baselineSource = 'vendor';
    }
  }

  // Fallback: rolling 90-day average across all vendors.
  if (!baselineLine) {
    const within90 = history.filter(r => {
      if (!r.invoice_date || !currentInvoiceDate) return false;
      return daysBetween(r.invoice_date, currentInvoiceDate) <= VENDOR_BASELINE_DAYS;
    });
    if (within90.length > 0) {
      const prices = within90.map(priceForCompare).filter(p => p != null);
      if (prices.length > 0) {
        const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
        baselineLine = {
          ...within90[0], // for invoice_id / date traceability (most recent)
          _syntheticAvgPrice: avg,
        };
        baselineSource = 'rolling_90d';
      }
    }
  }

  if (!baselineLine) {
    // History exists but nothing in the lookback window — treat as new.
    return {
      class: 'new_item',
      baseline_price: null,
      baseline_source: null,
      current_price: currentPrice,
      delta_abs: null,
      delta_pct: null,
      pack_changed: packChangedBlock,
      consecutive_increases: 0,
      baseline_invoice_id: null,
      baseline_invoice_date: null,
    };
  }

  const baselinePrice = baselineSource === 'rolling_90d'
    ? baselineLine._syntheticAvgPrice
    : priceForCompare(baselineLine);

  const deltaPct = pctDelta(currentPrice, baselinePrice);
  const deltaAbs = (currentPrice != null && baselinePrice != null)
    ? Number(currentPrice) - Number(baselinePrice)
    : null;

  // ── Classification ─────────────────────────────────────────────────────
  // pack_changed wins over up/down — that's the more interesting story.
  let klass;
  if (packChanged) {
    klass = 'pack_changed';
  } else if (deltaPct == null) {
    klass = 'flat'; // can't compute → treat as flat (don't crow about nothing)
  } else if (Math.abs(deltaPct) < FLAT_THRESHOLD_PCT) {
    klass = 'flat';
  } else if (deltaPct > 0) {
    klass = 'up';
  } else {
    klass = 'down';
  }

  // ── consecutive_increases (only meaningful when class is 'up') ─────────
  const consecutive = klass === 'up'
    ? computeConsecutiveIncreases(line.food_item_id, currentInvoiceDate, history, CONSECUTIVE_INCREASES_CAP)
    : 0;

  return {
    class: klass,
    baseline_price: baselinePrice != null ? Number(baselinePrice) : null,
    baseline_source: baselineSource,
    current_price: currentPrice,
    delta_abs: deltaAbs,
    delta_pct: deltaPct,
    pack_changed: packChangedBlock,
    consecutive_increases: consecutive,
    baseline_invoice_id: baselineLine.invoice_id || null,
    baseline_invoice_date: baselineLine.invoice_date || null,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// computeConsecutiveIncreases
//
// Walk back through the last `capN` purchases of this food_item across
// all vendors. Count the streak of up-moves ending at (but not including)
// the current invoice. Adding the current up-move on top, the operator
// sees consecutive_increases = streak + 1 ⇒ that's the value we return.
//
// Implementation note: we only return the streak that *ends* at the
// current invoice. If history shows up-up-down-up the answer is 1, not 2.
// This is what the UI wants — "how many invoices in a row, right now,
// is this thing creeping up?"
export function computeConsecutiveIncreases(foodItemId, currentInvoiceDate, history, capN = CONSECUTIVE_INCREASES_CAP) {
  if (!history || history.length === 0) return 1; // current is the first up
  const sorted = history
    .filter(r => r.food_item_id === foodItemId && r.invoice_date && currentInvoiceDate)
    .filter(r => new Date(r.invoice_date) < new Date(currentInvoiceDate))
    .sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date))
    .slice(0, capN);

  if (sorted.length === 0) return 1;

  let streak = 1; // current line itself counts as 1
  for (let i = 0; i < sorted.length - 1; i++) {
    const newer = priceForCompare(sorted[i]);
    const older = priceForCompare(sorted[i + 1]);
    const d = pctDelta(newer, older);
    if (d != null && d > FLAT_THRESHOLD_PCT) {
      streak += 1;
    } else {
      break;
    }
  }
  return streak;
}

// ───────────────────────────────────────────────────────────────────────────
// computeInvoiceSummary
//
// Roll up an array of parse_diff blobs into the parse_summary written to
// invoices.parse_summary. This is what the toast and the modal header
// read — pre-computed so no view-time work is needed.
//
// @returns {
//   total_lines, flat, up, down, pack_changed, new_item,
//   moved_count,                  // up + down + pack_changed + new_item
//   total_cost_change,            // sum of delta_abs * quantity (USD)
//   max_increase_pct,             // most extreme up move (or null)
//   max_decrease_pct,             // most extreme down move (or null)
//   has_red_flag,                 // any line with consecutive_increases ≥ 3
//   pack_change_examples,         // array of { vendor, name, from, to }
//                                 // first 3, for the toast subtitle
// }
export function computeInvoiceSummary(lineDiffs, lines = []) {
  const counts = { flat: 0, up: 0, down: 0, pack_changed: 0, new_item: 0 };
  let totalCostChange = 0;
  let maxIncrease = null;
  let maxDecrease = null;
  let hasRedFlag = false;
  const packExamples = [];

  for (let i = 0; i < lineDiffs.length; i++) {
    const d = lineDiffs[i];
    const l = lines[i] || {};
    if (!d || !d.class) continue;
    counts[d.class] = (counts[d.class] || 0) + 1;

    if (d.delta_abs != null && l.quantity != null) {
      totalCostChange += Number(d.delta_abs) * Number(l.quantity);
    }
    if (d.delta_pct != null && d.delta_pct > 0) {
      if (maxIncrease == null || d.delta_pct > maxIncrease) maxIncrease = d.delta_pct;
    }
    if (d.delta_pct != null && d.delta_pct < 0) {
      if (maxDecrease == null || d.delta_pct < maxDecrease) maxDecrease = d.delta_pct;
    }
    if (d.consecutive_increases && d.consecutive_increases >= CONSECUTIVE_RED_THRESHOLD) {
      hasRedFlag = true;
    }
    if (d.class === 'pack_changed' && d.pack_changed && packExamples.length < 3) {
      packExamples.push({
        vendor: l.vendor_name || null,
        name: l.normalized_name || l.raw_description || null,
        from: d.pack_changed.from,
        to: d.pack_changed.to,
      });
    }
  }

  return {
    total_lines: lineDiffs.length,
    flat: counts.flat,
    up: counts.up,
    down: counts.down,
    pack_changed: counts.pack_changed,
    new_item: counts.new_item,
    moved_count: counts.up + counts.down + counts.pack_changed + counts.new_item,
    total_cost_change: Math.round(totalCostChange * 100) / 100,
    max_increase_pct: maxIncrease,
    max_decrease_pct: maxDecrease,
    has_red_flag: hasRedFlag,
    pack_change_examples: packExamples,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// formatPackLabel
//
// Render "50#" / "1 ct" / "12×16oz" — whatever shape we have for this pack.
// Conservative: prefer pack_count + piece_weight when both present, fall
// back to either alone, fall back to "?" if neither.
export function formatPackLabel(packCount, pieceWeight) {
  const pc = packCount != null ? String(packCount) : null;
  const pw = pieceWeight != null && String(pieceWeight).trim() !== '' ? String(pieceWeight).trim() : null;
  if (pc && pw) return `${pc}×${pw}`;
  if (pw) return pw;
  if (pc) return `${pc} ct`;
  return '?';
}

// ───────────────────────────────────────────────────────────────────────────
// classToTone
//
// Single source of truth for diff-class → DESIGN_SYSTEM tone mapping.
// Consumed by Toast/Modal/Invoices preview chip — keep the mapping here
// so a tone change ripples to every surface.
export function classToTone(klass, consecutiveIncreases = 0) {
  switch (klass) {
    case 'up':
      return consecutiveIncreases >= CONSECUTIVE_RED_THRESHOLD ? 'red' : 'amber';
    case 'pack_changed':
      return 'amber';
    case 'down':
      return 'green';
    case 'new_item':
      return 'neutral';
    case 'flat':
    default:
      return 'neutral';
  }
}
